/**
 * SavedPlansView.js
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { SavedPlans } from '../utils/SavedPlans.js'
import { SavedDays } from '../utils/SavedDays.js'
import { Store } from '../store/Store.js'
import { i18n } from '../i18n/index.js'
import { escapeHtml } from '../utils/viewUtils.js'

export class SavedPlansView {
  constructor(container) {
    this.container = container
    this.renamingPlanId = null
    // Which list the rename dialog and the file input are working on
    this.renamingKind = 'plan'
    this.importKind = 'plan'
    // Days read once per render, so the card and its handlers see the same list
    this.days = []
    // FileReader of an import in progress, aborted by destroy()
    this.importReader = null
  }

  t(key, params) {
    return i18n.t(key, params)
  }

  destroy() {
    // An import still reading when the user navigates away would call
    // render() on a detached container, so abort it
    if (this.importReader) {
      try {
        this.importReader.abort()
      } catch (err) {
        console.error('SavedPlansView.destroy: aborting the pending import failed', err)
      }
      this.importReader = null
    }
    // Nothing else to unwire: no store subscription, no timer and no listener
    // outside this view's container. The hidden file input and the rename
    // dialog are part of container.innerHTML, so their listeners are released
    // together with the container content.
    this.container = null
    this.renamingPlanId = null
    this.renamingKind = null
    this.importKind = null
    this.days = null
  }

  render() {
    const plans = SavedPlans.getAll()
    this.days = SavedDays.getAll()

    this.container.innerHTML = `
      <div class="space-y-4 pb-20">
        <div class="card bg-base-100 shadow-sm">
          <div class="card-body p-4">
            <div class="flex justify-between items-center">
              <h2 class="card-title text-sm">${this.t('saved_plans_title')}</h2>
              <button id="import-plan-btn" class="btn btn-ghost btn-xs">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                ${this.t('saved_plans_import')}
              </button>
            </div>

            ${plans.length === 0 ? `
              <div class="text-center py-8 opacity-70">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                </svg>
                <p>${this.t('saved_plans_empty')}</p>
                <p class="text-xs mt-1">${this.t('saved_plans_empty_hint')}</p>
              </div>
            ` : `
              <div class="space-y-2 mt-2">
                ${plans.map(plan => this.renderPlanCard(plan)).join('')}
              </div>
            `}
          </div>
        </div>
      </div>

      ${this.renderDaysCard()}

      <!-- Hidden file input for import -->
      <input type="file" id="import-file-input" accept="application/json,text/json,text/plain,.json,*/*" class="hidden" />

      <!-- Rename Modal -->
      <dialog id="rename-modal" class="modal">
        <div class="modal-box">
          <h3 class="font-bold text-lg">${this.t('saved_plans_rename')}</h3>
          <div class="form-control mt-4">
            <input type="text" id="rename-input" class="input input-bordered" placeholder="${this.t('saved_plans_name_placeholder')}" />
          </div>
          <div class="modal-action">
            <button id="cancel-rename-btn" class="btn btn-ghost">${this.t('cancel')}</button>
            <button id="confirm-rename-btn" class="btn btn-primary">${this.t('save')}</button>
          </div>
        </div>
        <form method="dialog" class="modal-backdrop"><button>close</button></form>
      </dialog>
    `

    this.bindEvents()
  }

  // Days of diving saved under a name. A list of their own: a day and a single
  // dive plan do not carry the same thing, and every reader of the plan list
  // expects one dive with its segments.
  renderDaysCard() {
    const days = this.days || []

    return `
      <div class="card bg-base-100 shadow-sm">
        <div class="card-body p-4">
          <div class="flex justify-between items-center">
            <h2 class="card-title text-sm">${this.t('saved_days_title')}</h2>
            <button id="import-day-btn" class="btn btn-ghost btn-xs">${this.t('saved_plans_import')}</button>
          </div>

          ${days.length === 0 ? `
            <div class="text-center py-6 opacity-70">
              <p>${this.t('saved_days_empty')}</p>
              <p class="text-xs mt-1">${this.t('saved_days_empty_hint')}</p>
            </div>
          ` : `
            <div class="space-y-2 mt-2">
              ${days.map(day => this.renderDayCard(day)).join('')}
            </div>
          `}
        </div>
      </div>
    `
  }

  renderDayCard(day) {
    const date = new Date(day.createdAt).toLocaleDateString()
    const summary = day.summary || {}
    // Figures frozen when the day was saved, never recomputed by this list,
    // and absent when the day was saved before being computed
    const totals = Number.isFinite(summary.cnsTotal)
      ? ` | ${this.t('plan_cns_series')} ${summary.cnsTotal}%`
      : ``

    return `
      <div class="bg-base-200 rounded-lg p-3">
        <div class="flex justify-between items-start">
          <div class="flex-1 min-w-0">
            <div class="font-medium truncate">${escapeHtml(day.name)}</div>
            <div class="text-xs opacity-70 mt-1">
              ${this.t('day_dives_count')}: ${summary.diveCount ?? day.dives?.length ?? 0}
              ${summary.maxDepth ? ` | ${summary.maxDepth}m` : ''}
              ${totals}
            </div>
            <div class="text-xs opacity-50 mt-1">GF ${summary.gf || '-'} | ${date}</div>
          </div>
          <div class="dropdown dropdown-end">
            <label tabindex="0" class="btn btn-ghost btn-xs btn-circle">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </label>
            <ul tabindex="0" class="dropdown-content z-10 menu p-2 shadow bg-base-100 rounded-box w-40">
              <li><a class="rename-day-btn" data-id="${day.id}">${this.t('saved_plans_rename')}</a></li>
              <li><a class="duplicate-day-btn" data-id="${day.id}">${this.t('saved_plans_duplicate')}</a></li>
              <li><a class="export-day-btn" data-id="${day.id}">${this.t('saved_plans_export')}</a></li>
              <li><a class="delete-day-btn text-error" data-id="${day.id}">${this.t('delete')}</a></li>
            </ul>
          </div>
        </div>
        <div class="flex gap-2 mt-3">
          <button class="btn btn-primary btn-sm flex-1 load-day-btn" data-id="${day.id}">
            ${this.t('saved_plans_load')}
          </button>
        </div>
      </div>
    `
  }

  // Label of a water type, in the two forms the engine accepts: the name of a
  // preset, or the density itself when the diver typed one.
  waterLabel(waterType) {
    const preset = Store.waterPresetKey(waterType)
    if (preset !== Store.CUSTOM_WATER) {
      const labelKey = Store.WATER_PRESET_LABEL_KEYS[preset]
      if (!labelKey) {
        console.error('SavedPlansView.waterLabel: no label key declared for the water preset "%s"', preset)
        return preset
      }
      return this.t(labelKey)
    }
    const density = Store.waterDensityOrNull(waterType)
    if (density === null) {
      // Store.waterDensityOrNull logged the reason
      return '--'
    }
    return `${density} g/l`
  }

  // Water and altitude the plan was computed in.
  // Two plans with the same depths and the same times do not have the same
  // decompression in a mountain lake and in the Red Sea, so the list is where
  // the diver tells them apart before loading one. The line is always shown,
  // and made to stand out as soon as the site is not sea level in sea water.
  renderEnvironmentLine(plan) {
    const { environment, errorKey } = SavedPlans.readEnvironment(plan)
    const label = this.t('saved_plan_environment')

    if (errorKey) {
      // SavedPlans.readEnvironment logged the exact reason. Showing '--'
      // rather than a plausible site: this plan will be refused on load.
      return `<div class="text-xs text-error mt-1">${label}: --</div>`
    }

    const isDefault = environment.altitude === SavedPlans.LEGACY_ENVIRONMENT.altitude &&
      environment.waterType === SavedPlans.LEGACY_ENVIRONMENT.waterType
    const style = isDefault ? 'opacity-60' : 'font-medium text-info'
    return `<div class="text-xs mt-1 ${style}">${label}: ${this.waterLabel(environment.waterType)}, ${environment.altitude} m</div>`
  }

  renderPlanCard(plan) {
    const date = new Date(plan.createdAt).toLocaleDateString()
    const summary = plan.summary || {}
    // The runtime shown here is the one computed when the plan was saved: the
    // list recomputes nothing. It is therefore never displayed alone but with
    // the date it was recorded on, right next to the water and the altitude it
    // was obtained in, which loading the plan restores exactly.
    const runtimeText = summary.runtime
      ? `${this.t('plan_runtime')}: ${summary.runtime} min (${date})`
      : date

    return `
      <div class="bg-base-200 rounded-lg p-3">
        <div class="flex justify-between items-start">
          <div class="flex-1 min-w-0">
            <div class="font-medium truncate">${escapeHtml(plan.name)}</div>
            <div class="text-xs opacity-70 mt-1">
              ${summary.maxDepth ? `${summary.maxDepth}m` : ''}
              ${summary.bottomTime ? `| ${summary.bottomTime} min ${this.t('plan_bottom').toLowerCase()}` : ''}
            </div>
            ${this.renderEnvironmentLine(plan)}
            <div class="text-xs opacity-50 mt-1">
              ${summary.mode || 'OC'} | GF ${summary.gf || '-'} | ${runtimeText}
            </div>
          </div>
          <div class="dropdown dropdown-end">
            <label tabindex="0" class="btn btn-ghost btn-xs btn-circle">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
              </svg>
            </label>
            <ul tabindex="0" class="dropdown-content z-10 menu p-2 shadow bg-base-100 rounded-box w-40">
              <li><a class="rename-plan-btn" data-id="${plan.id}">${this.t('saved_plans_rename')}</a></li>
              <li><a class="duplicate-plan-btn" data-id="${plan.id}">${this.t('saved_plans_duplicate')}</a></li>
              <li><a class="export-plan-btn" data-id="${plan.id}">${this.t('saved_plans_export')}</a></li>
              <li><a class="delete-plan-btn text-error" data-id="${plan.id}">${this.t('delete')}</a></li>
            </ul>
          </div>
        </div>
        <div class="flex gap-2 mt-3">
          <button class="btn btn-primary btn-sm flex-1 load-plan-btn" data-id="${plan.id}">
            ${this.t('saved_plans_load')}
          </button>
        </div>
      </div>
    `
  }

  bindEvents() {
    // Load plan
    this.container.querySelectorAll('.load-plan-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = parseInt(e.target.dataset.id)
        const result = SavedPlans.loadIntoStore(id, window.store)
        if (result.ok) {
          window.location.hash = '/'
          return
        }
        // SavedPlans.loadIntoStore logged the exact reason and left the store
        // untouched. The diver must be told the plan was NOT loaded instead of
        // watching the button do nothing and diving the plan on screen.
        console.error('SavedPlansView: plan id=%o was not loaded (%s)', id, result.reason)
        const detail = result.errorKey ? `\n${this.t(result.errorKey)}` : ''
        alert(`${this.t('plan_error_title')}${detail}`)
      })
    })

    // Rename plan
    this.container.querySelectorAll('.rename-plan-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault()
        const id = parseInt(e.target.dataset.id)
        const plan = SavedPlans.get(id)
        if (plan) {
          this.openRenameModal(id, plan.name)
        }
      })
    })

    // Duplicate plan
    this.container.querySelectorAll('.duplicate-plan-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault()
        const id = parseInt(e.target.dataset.id)
        SavedPlans.duplicate(id)
        this.render()
      })
    })

    // Export plan
    this.container.querySelectorAll('.export-plan-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault()
        const id = parseInt(e.target.dataset.id)
        SavedPlans.exportToJson(id)
      })
    })

    // Delete plan
    this.container.querySelectorAll('.delete-plan-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault()
        const id = parseInt(e.target.dataset.id)
        if (confirm(this.t('saved_plans_confirm_delete'))) {
          SavedPlans.delete(id)
          this.render()
        }
      })
    })

    // Load a day: the dive of the plan screen is left untouched, so the diver
    // is taken to the day screen, not to the plan one
    this.container.querySelectorAll('.load-day-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = parseInt(e.currentTarget.dataset.id, 10)
        const result = SavedDays.loadIntoStore(id, window.store)
        if (result.ok) {
          window.location.hash = '/day'
          return
        }
        // SavedDays.loadIntoStore logged the exact reason and left the store
        // untouched: the diver must be told the day was NOT loaded
        console.error('SavedPlansView: day id=%o was not loaded (%s)', id, result.reason)
        const detail = result.errorKey ? `\n${this.t(result.errorKey)}` : ''
        alert(`${this.t('plan_error_title')}${detail}`)
      })
    })

    this.container.querySelectorAll('.rename-day-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault()
        const id = parseInt(e.currentTarget.dataset.id, 10)
        const day = SavedDays.get(id)
        if (!day) {
          console.error('SavedPlansView: day id=%o no longer exists, nothing renamed', id)
          return
        }
        this.openRenameModal(id, day.name, 'day')
      })
    })

    this.container.querySelectorAll('.duplicate-day-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault()
        SavedDays.duplicate(parseInt(e.currentTarget.dataset.id, 10))
        this.render()
      })
    })

    this.container.querySelectorAll('.export-day-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault()
        SavedDays.exportToJson(parseInt(e.currentTarget.dataset.id, 10))
      })
    })

    this.container.querySelectorAll('.delete-day-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault()
        const id = parseInt(e.currentTarget.dataset.id, 10)
        if (confirm(this.t('saved_days_confirm_delete'))) {
          SavedDays.delete(id)
          this.render()
        }
      })
    })

    document.getElementById('import-day-btn')?.addEventListener('click', () => {
      this.importKind = 'day'
      document.getElementById('import-file-input')?.click()
    })

    // Import button
    document.getElementById('import-plan-btn')?.addEventListener('click', () => {
      this.importKind = 'plan'
      document.getElementById('import-file-input')?.click()
    })

    // File input for import
    document.getElementById('import-file-input')?.addEventListener('change', (e) => {
      const file = e.target.files?.[0]
      if (!file) {
        console.error('SavedPlansView: import triggered without a file, nothing to read')
        return
      }

      const reader = new FileReader()
      this.importReader = reader

      reader.onload = (event) => {
        this.importReader = null
        const isDay = this.importKind === 'day'
        const result = isDay
          ? SavedDays.importFromJson(event.target.result)
          : SavedPlans.importFromJson(event.target.result)
        if (result) {
          this.render()
        } else {
          // Both importers log the precise reason before returning null
          console.error('SavedPlansView: "%s" is not a valid mvplan %s, import aborted', file.name, isDay ? 'day' : 'plan')
          alert(this.t(isDay ? 'saved_days_import_error' : 'saved_plans_import_error'))
        }
      }

      reader.onerror = () => {
        this.importReader = null
        console.error('SavedPlansView: reading "%s" failed', file.name, reader.error)
        // Same list as the one being imported: a day that cannot be read must
        // not tell the diver his plan failed
        alert(this.t(this.importKind === 'day' ? 'saved_days_import_error' : 'saved_plans_import_error'))
      }

      reader.readAsText(file)

      // Cleared so the same file can be picked again: without this, a second
      // selection of the same file fires no change event and the button does
      // nothing at all. Two buttons share this input, which makes it likely.
      e.target.value = ''
    })

    // Rename modal handlers
    document.getElementById('cancel-rename-btn')?.addEventListener('click', () => {
      document.getElementById('rename-modal')?.close()
    })

    document.getElementById('confirm-rename-btn')?.addEventListener('click', () => {
      const newName = document.getElementById('rename-input')?.value
      if (!newName || !this.renamingPlanId) {
        console.error('SavedPlansView: rename confirmed with no name or no target, nothing renamed')
        return
      }
      if (this.renamingKind === 'day') {
        SavedDays.rename(this.renamingPlanId, newName)
      } else {
        SavedPlans.rename(this.renamingPlanId, newName)
      }
      document.getElementById('rename-modal')?.close()
      this.render()
    })
  }

  openRenameModal(id, currentName, kind = 'plan') {
    this.renamingPlanId = id
    this.renamingKind = kind
    const input = document.getElementById('rename-input')
    if (input) {
      input.value = currentName
    }
    document.getElementById('rename-modal')?.showModal()
  }
}
